interface CachedDocumentation {
  description: string;
  attachments: Array<{
    attachmentId: string;
    fileName: string;
    mimeType: string;
    attachmentUri: string;
  }>;
  createdAt: string;
  serviceOfferId: number;
}

export function useDocumentationCache() {
  const CACHE_KEY = "documentation_cache";
  const CACHE_EXPIRY_HOURS = 5 / 60; // Cache expires after 5 minutes

  const getCachedDocumentation = (
    serviceOfferId: number,
  ): CachedDocumentation | null => {
    try {
      const cached = localStorage.getItem(`${CACHE_KEY}_${serviceOfferId}`);
      if (!cached) {
        return null;
      }

      const parsed = JSON.parse(cached);

      // Check if cache has expired
      const expiryTime = new Date(parsed.expiryTime);
      if (new Date() > expiryTime) {
        clearCachedDocumentation(serviceOfferId);
        return null;
      }

      return parsed.data;
    } catch (error) {
      console.error("Failed to retrieve cached documentation:", error);
      return null;
    }
  };

  const setCachedDocumentation = (data: CachedDocumentation): void => {
    try {
      const expiryTime = new Date();
      expiryTime.setHours(expiryTime.getHours() + CACHE_EXPIRY_HOURS);

      const cacheEntry = {
        data,
        expiryTime: expiryTime.toISOString(),
      };

      localStorage.setItem(
        `${CACHE_KEY}_${data.serviceOfferId}`,
        JSON.stringify(cacheEntry),
      );
    } catch (error) {
      console.error("❌ Cache: Failed to cache documentation:", error);
    }
  };

  const clearCachedDocumentation = (serviceOfferId: number): void => {
    try {
      localStorage.removeItem(`${CACHE_KEY}_${serviceOfferId}`);
    } catch (error) {
      console.error("Failed to clear cached documentation:", error);
    }
  };

  const clearAllDocumentationCache = (): void => {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach((key) => {
        if (key.startsWith(CACHE_KEY)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error("Failed to clear all documentation cache:", error);
    }
  };

  return {
    getCachedDocumentation,
    setCachedDocumentation,
    clearCachedDocumentation,
    clearAllDocumentationCache,
  };
}
